import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import webSocketService from '../services/webSocketService'

/**
 * Хук для работы с WebSocket соединением
 */
export const useWebSocket = () => {
    const [connectionStatus, setConnectionStatus] = useState({
        isConnected: false,
        socketId: null,
        reconnectAttempts: 0
    })
    
    const user = useSelector(state => state.store.user)
    const isInitialized = useRef(false)

    useEffect(() => {
        // Инициализируем WebSocket только один раз
        if (!isInitialized.current && user.isAuth) {
            console.log('Инициализация WebSocket для аутентифицированного пользователя')
            webSocketService.connect()
            isInitialized.current = true
        } else if (!user.isAuth && isInitialized.current) {
            // Отключаемся при выходе из системы
            console.log('Отключение WebSocket при выходе из системы')
            webSocketService.disconnect()
            isInitialized.current = false
        }

        // Обновляем статус подключения
        const updateStatus = () => {
            setConnectionStatus(webSocketService.getConnectionStatus())
        }

        // Обновляем статус при подключении/отключении
        webSocketService.on('connect', updateStatus)
        webSocketService.on('disconnect', updateStatus)

        // Обновляем статус каждые 5 секунд
        const statusInterval = setInterval(updateStatus, 5000)

        return () => {
            webSocketService.off('connect', updateStatus)
            webSocketService.off('disconnect', updateStatus)
            clearInterval(statusInterval)
        }
    }, [user.isAuth])

    // Очистка при размонтировании компонента
    useEffect(() => {
        return () => {
            // Не отключаем WebSocket при размонтировании компонента,
            // так как он может использоваться в других компонентах
            console.log('WebSocket хук размонтирован')
        }
    }, [])

    return {
        connectionStatus,
        emit: webSocketService.emit.bind(webSocketService),
        on: webSocketService.on.bind(webSocketService),
        off: webSocketService.off.bind(webSocketService),
        disconnect: webSocketService.disconnect.bind(webSocketService),
        reconnect: webSocketService.connect.bind(webSocketService)
    }
}

/**
 * Хук для подписки на обновления состояния
 */
export const useStateUpdates = () => {
    const [lastUpdate, setLastUpdate] = useState(null)
    const [updateCount, setUpdateCount] = useState(0)

    useEffect(() => {
        const handleStateUpdate = (data) => {
            setLastUpdate(new Date().toISOString())
            setUpdateCount(prev => prev + 1)
            console.log('Обновление состояния получено через хук:', data.timestamp)
        }

        webSocketService.on('state_update', handleStateUpdate)

        return () => {
            webSocketService.off('state_update', handleStateUpdate)
        }
    }, [])

    return {
        lastUpdate,
        updateCount
    }
}

/**
 * Хук для отправки команд через WebSocket
 */
export const useWebSocketCommands = () => {
    const emit = webSocketService.emit.bind(webSocketService)

    const sendCommand = (command, data = {}) => {
        emit(command, data)
    }

    const getState = () => {
        emit('state:getAll', {})
    }

    const forceBroadcast = () => {
        emit('state:forceBroadcast', {})
    }

    const getHostInfo = (hostid) => {
        emit('state:getHostById', { hostid })
    }

    const testPing = () => {
        emit('test:ping', {})
    }

    return {
        sendCommand,
        getState,
        forceBroadcast,
        getHostInfo,
        testPing
    }
}
